import React, { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { User, Users, Calendar, RotateCcw, Printer } from 'lucide-react';
import { ActiveTechnician, TechnicianAssignment } from '@/lib/supabase/technicianAssignments';
import { DatePicker } from '@/components/ui/date-picker';
import { cn } from '@/lib/utils';
import {
  CONTROL_DECK,
  CONTROL_DECK_ENTER,
  CONTROL,
  activeCls,
  SECTION_LABEL,
  ValuePill
} from '@/components/ui/control-deck';

interface TechnicianDropdownProps {
  technicians: ActiveTechnician[];
  assignments: TechnicianAssignment[];
  selectedTechnicianId: string | null;
  onTechnicianChange: (technicianId: string) => void;
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  filterMode: 'all' | 'assigned' | 'unassigned';
  onFilterModeChange: (mode: 'all' | 'assigned' | 'unassigned') => void;
  className?: string;
  // Print route button props
  onPrintRoute?: () => void;
}

interface TechnicianWithCount extends ActiveTechnician {
  assignmentCount: number;
}

/**
 * Technician dropdown selector with assignment counts
 * Orders technicians alphabetically by name
 */
export const TechnicianDropdown: React.FC<TechnicianDropdownProps> = ({
  technicians,
  assignments,
  selectedTechnicianId,
  onTechnicianChange,
  selectedDate,
  onDateChange,
  filterMode,
  onFilterModeChange,
  className = '',
  onPrintRoute
}) => {
  // Calculate assignment counts per technician
  const techniciansWithCounts = useMemo((): TechnicianWithCount[] => {
    const countMap = new Map<string, number>();

    // Count assignments per technician (only non-completed/cancelled)
    assignments.forEach(assignment => {
      if (
        assignment.assignment_status !== 'completed' &&
        assignment.assignment_status !== 'cancelled'
      ) {
        const current = countMap.get(assignment.technician_id) || 0;
        countMap.set(assignment.technician_id, current + 1);
      }
    });

    // Add counts to technicians and sort alphabetically by name
    return technicians
      .filter(tech => tech.is_active)
      .map(tech => ({
        ...tech,
        assignmentCount: countMap.get(tech.id) || 0
      }))
      .sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [technicians, assignments]);

  // Get selected technician info
  const selectedTechnician = useMemo(() => {
    return techniciansWithCounts.find(t => t.id === selectedTechnicianId);
  }, [techniciansWithCounts, selectedTechnicianId]);

  // Get skill level badge color
  const getSkillLevelColor = (skillLevel: number): string => {
    switch (skillLevel) {
      case 3:
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 2:
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      case 1:
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getSkillLevelLabel = (skillLevel: number): string => {
    switch (skillLevel) {
      case 3:
        return 'High';
      case 2:
        return 'Medium';
      case 1:
        return 'Low';
      default:
        return 'Unset';
    }
  };

  // Handle date change with fallback to today
  const handleDateChange = (date: Date | undefined) => {
    onDateChange(date || new Date());
  };

  // Get tomorrow's date for max date constraint (can schedule up to 30 days ahead)
  const maxDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date;
  }, []);

  // Check if selected date is today
  const isToday = useMemo(() => {
    const today = new Date();
    return (
      selectedDate.getDate() === today.getDate() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getFullYear() === today.getFullYear()
    );
  }, [selectedDate]);

  // Reset to today
  const handleResetToToday = () => {
    onDateChange(new Date());
  };

  // Get dates with assignments for the selected technician
  const technicianAssignmentDates = useMemo((): Date[] => {
    if (!selectedTechnicianId) return [];

    const dates: Date[] = [];
    const seenDates = new Set<string>();

    assignments.forEach(assignment => {
      if (
        assignment.technician_id === selectedTechnicianId &&
        assignment.expected_visit_date &&
        assignment.assignment_status !== 'completed' &&
        assignment.assignment_status !== 'cancelled'
      ) {
        // Extract YYYY-MM-DD and parse as local date to avoid UTC timezone shift
        // Database returns TIMESTAMPTZ like "2026-01-27T00:00:00+00:00"
        const dateStr = assignment.expected_visit_date;
        const dateMatch = typeof dateStr === 'string' && dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);

        let date: Date;
        if (dateMatch && dateMatch[1] && dateMatch[2] && dateMatch[3]) {
          const year = parseInt(dateMatch[1], 10);
          const month = parseInt(dateMatch[2], 10) - 1; // JS months are 0-indexed
          const day = parseInt(dateMatch[3], 10);
          date = new Date(year, month, day, 12, 0, 0); // Noon local time
        } else {
          date = new Date(dateStr);
        }

        const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

        if (!seenDates.has(dateKey)) {
          seenDates.add(dateKey);
          dates.push(date);
        }
      }
    });

    return dates;
  }, [selectedTechnicianId, assignments]);

  return (
    <div className={cn('px-3 py-2 m-2', CONTROL_DECK, CONTROL_DECK_ENTER, className)}>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        {/* Left side - Technician & Date Selection */}
        <div className='flex flex-wrap items-center gap-4 flex-1'>
          {/* Technician Selector */}
          <div className='flex items-center gap-3'>
            <div className={SECTION_LABEL}>
              <User className='h-4 w-4' />
              <span>Technician</span>
            </div>

            <Select value={selectedTechnicianId || ''} onValueChange={onTechnicianChange}>
              <SelectTrigger
                className={cn('h-8 w-[280px] text-xs', CONTROL, activeCls(!!selectedTechnicianId))}
              >
                <SelectValue placeholder='Select a technician'>
                  {selectedTechnician && (
                    // `!flex` defeats shadcn SelectTrigger's `[&>span]:line-clamp-1`, which
                    // clips the pill in Safari (matches the recall bar's ControlLabel fix).
                    <span className='!flex items-center gap-1.5 truncate'>
                      <span className='truncate'>{selectedTechnician.full_name}</span>
                      <ValuePill value={`${selectedTechnician.assignmentCount} assigned`} />
                    </span>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {techniciansWithCounts.length === 0 ? (
                  <div className='py-6 text-center text-sm text-muted-foreground'>
                    <Users className='h-8 w-8 mx-auto mb-2 opacity-50' />
                    No active technicians available
                  </div>
                ) : (
                  techniciansWithCounts.map(tech => (
                    <SelectItem key={tech.id} value={tech.id} className='py-2 group'>
                      <div className='flex items-center justify-between gap-4 w-full'>
                        <div className='flex items-center gap-2'>
                          <span className='font-medium'>{tech.full_name}</span>
                          <Badge
                            variant='outline'
                            className={`text-xs px-1.5 py-0 ${getSkillLevelColor(tech.skill_level)}`}
                          >
                            {getSkillLevelLabel(tech.skill_level)}
                          </Badge>
                        </div>
                        <span className='text-xs text-muted-foreground group-data-[highlighted]:text-accent-foreground'>
                          {tech.assignmentCount} assigned
                        </span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Schedule Date Picker */}
          <div className='flex items-center gap-3'>
            <div className={SECTION_LABEL}>
              <Calendar className='h-4 w-4' />
              <span>Date</span>
            </div>
            <div className='w-[200px]'>
              <DatePicker
                value={selectedDate}
                onChange={handleDateChange}
                placeholder='Select date'
                fromDate={new Date()}
                toDate={maxDate}
                highlightedDates={technicianAssignmentDates}
              />
            </div>
            {!isToday && (
              <Button
                variant='outline'
                size='sm'
                className='h-8 px-3 text-xs gap-1.5'
                onClick={handleResetToToday}
              >
                <RotateCcw className='h-3 w-3' />
                Today
              </Button>
            )}
          </div>
        </div>

        {/* Right side - Controls */}
        <div className='flex items-center gap-4'>
          {/* Print Route Button */}
          {onPrintRoute && (
            <Button
              size='default'
              onClick={onPrintRoute}
              disabled={!selectedTechnician || selectedTechnician.assignmentCount === 0}
              className='h-8 px-4 text-sm font-semibold gap-2'
            >
              <Printer className='h-4 w-4' />
              Print Route
            </Button>
          )}

          {/* Filter Mode Toggle */}
          <div className='flex items-center gap-1 bg-muted rounded-lg p-1'>
            {(['assigned', 'unassigned', 'all'] as const).map(mode => (
              <Button
                key={mode}
                variant={filterMode === mode ? 'default' : 'ghost'}
                size='sm'
                className={cn(
                  'h-7 px-3 text-xs capitalize rounded-md transition-all',
                  filterMode === mode
                    ? 'bg-primary text-primary-foreground shadow-sm font-medium'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                onClick={() => onFilterModeChange(mode)}
              >
                {mode}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicianDropdown;
